import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, Shield, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import PadaidLogo from '../../components/ui/PadaidLogo';
import { 
  requestPasswordReset,
  sendVerificationCode,
  verifyResetCode,
  updateFounderPassword,
  requestPasswordResetLink
} from '../../services/passwordReset';

const ForgotPassword = () => {
  const [step, setStep] = useState('identify');
  const [identifier, setIdentifier] = useState('');
  const [resetMethod, setResetMethod] = useState('email');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const founderData = { email: 'abakar.ali@padaid.com', phone: '06 22 88 87 21', name: 'Abakar ALI' };

  const handleIdentifierSubmit = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      await requestPasswordReset(identifier);
      setStep('method');
    } catch (err) {
      setError(err.response?.data?.detail || 'Email ou numéro non reconnu. Réservé au fondateur.');
    } finally { setLoading(false); }
  };

  const handleMethodSubmit = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      if (resetMethod === 'email') {
        // En parallèle, déclencher l'envoi d'un lien sécurisé si compte existe
        try { await requestPasswordResetLink(identifier); } catch(e){}
      }
      await sendVerificationCode(identifier, resetMethod);
      setStep('sent');
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'envoi du code");
    } finally { setLoading(false); }
  };

  const handleVerificationSubmit = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      await verifyResetCode(identifier, verificationCode);
      setStep('reset');
    } catch (err) {
      setError(err.response?.data?.detail || 'Code invalide ou expiré');
    } finally { setLoading(false); }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      await updateFounderPassword(identifier, verificationCode, e.target.newPassword.value);
      setStep('success');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la réinitialisation');
    } finally { setLoading(false); }
  };

  const renderIdentifyStep = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center"><Shield className="mr-2 h-5 w-5 text-destructive" />Réinitialisation Sécurisée</CardTitle>
        <CardDescription>Entrez votre email ou téléphone pour réinitialiser votre mot de passe</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleIdentifierSubmit} className="space-y-4">
          <div className="flex justify-end">
            <Button type="button" variant="ghost" className="text-sm" asChild>
              <Link to="/reset-password">Vous avez reçu un lien ? Ouvrir la page</Link>
            </Button>
          </div>
          <div>
            <Label htmlFor="identifier">Email ou Téléphone</Label>
            <Input id="identifier" type="text" value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder="abakar.ali@padaid.com ou 06 22 88 87 21" required className="mt-1" />
          </div>
          {error && (<Alert className="border-destructive/20 bg-destructive/5"><AlertTriangle className="h-4 w-4 text-destructive" /><AlertDescription className="text-destructive">{error}</AlertDescription></Alert>)}
          <Button type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={loading}>{loading ? 'Vérification...' : 'Continuer'}</Button>
        </form>
        <div className="mt-4 p-3 bg-muted rounded-lg"><p className="text-sm text-muted-foreground"><strong>Accès réservé au fondateur :</strong><br />Email : {founderData.email}<br />Téléphone : {founderData.phone}</p></div>
      </CardContent>
    </Card>
  );

  const renderMethodStep = () => (
    <Card>
      <CardHeader>
        <CardTitle>Méthode de Récupération</CardTitle>
        <CardDescription>Choisissez comment recevoir votre code</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleMethodSubmit} className="space-y-4">
          <div className="flex justify-end">
            <Button type="button" variant="outline" asChild>
              <Link to={identifier ? `/reset-password?email=${encodeURIComponent(identifier)}` : '/reset-password'}>Recevoir un lien par email</Link>
            </Button>
          </div>
          <RadioGroup value={resetMethod} onValueChange={setResetMethod}>
            <div className="flex items-center space-x-2 p-3 border rounded-lg"><RadioGroupItem value="email" id="email" /><Label htmlFor="email" className="flex items-center cursor-pointer flex-1"><Mail className="mr-2 h-4 w-4 text-primary" /><div><div className="font-medium">Par Email</div><div className="text-sm text-muted-foreground">Code envoyé à {founderData.email}</div></div></Label></div>
            <div className="flex items-center space-x-2 p-3 border rounded-lg"><RadioGroupItem value="sms" id="sms" /><Label htmlFor="sms" className="flex items-center cursor-pointer flex-1"><Phone className="mr-2 h-4 w-4 text-secondary" /><div><div className="font-medium">Par SMS</div><div className="text-sm text-muted-foreground">Code envoyé au {founderData.phone}</div></div></Label></div>
          </RadioGroup>
          {error && (<Alert className="border-destructive/20 bg-destructive/5"><AlertTriangle className="h-4 w-4 text-destructive" /><AlertDescription className="text-destructive">{error}</AlertDescription></Alert>)}
          <div className="flex space-x-3"><Button type="button" variant="outline" onClick={() => setStep('identify')} className="flex-1">Retour</Button><Button type="submit" className="flex-1 bg-primary hover:bg-primary/90" disabled={loading}>{loading ? 'Envoi...' : 'Envoyer le Code'}</Button></div>
        </form>
      </CardContent>
    </Card>
  );

  const renderSentStep = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center"><CheckCircle className="mr-2 h-5 w-5 text-green-500" />Code Envoyé</CardTitle>
        <CardDescription>Un code de vérification a été envoyé</CardDescription>
      </CardHeader>
      <CardContent>
        <Alert className="mb-4 border-green-500/20 bg-green-500/5"><CheckCircle className="h-4 w-4 text-green-500" /><AlertDescription>Code envoyé {resetMethod === 'email' ? `par email à ${founderData.email}` : `par SMS au ${founderData.phone}`}</AlertDescription></Alert>
        <form onSubmit={handleVerificationSubmit} className="space-y-4">
          <div><Label htmlFor="code">Code de Vérification</Label><Input id="code" type="text" value={verificationCode} onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="123456" maxLength={6} className="mt-1 text-center text-lg font-mono" required /></div>
          {error && (<Alert className="border-destructive/20 bg-destructive/5"><AlertTriangle className="h-4 w-4 text-destructive" /><AlertDescription className="text-destructive">{error}</AlertDescription></Alert>)}
          <div className="flex space-x-3"><Button type="button" variant="outline" onClick={() => setStep('method')} className="flex-1">Retour</Button><Button type="submit" className="flex-1 bg-primary hover:bg-primary/90" disabled={loading}>{loading ? 'Vérification...' : 'Vérifier'}</Button></div>
        </form>
        <div className="mt-4 text-center"><Button variant="ghost" size="sm" onClick={() => setStep('method')}>Renvoyer le code</Button></div>
      </CardContent>
    </Card>
  );

  const renderResetStep = () => (
    <Card>
      <CardHeader>
        <CardTitle>Nouveau Mot de Passe</CardTitle>
        <CardDescription>Choisissez un nouveau mot de passe sécurisé</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handlePasswordReset} className="space-y-4">
          <div><Label htmlFor="newPassword">Nouveau Mot de Passe</Label><Input id="newPassword" name="newPassword" type="password" minLength={8} required className="mt-1" /></div>
          <div className="p-3 bg-muted rounded-lg"><p className="text-sm text-muted-foreground"><strong>Exigences du mot de passe :</strong><br />• Au moins 8 caractères<br />• Recommandé : lettres, chiffres et symboles</p></div>
          {error && (<Alert className="border-destructive/20 bg-destructive/5"><AlertTriangle className="h-4 w-4 text-destructive" /><AlertDescription className="text-destructive">{error}</AlertDescription></Alert>)}
          <Button type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={loading}>{loading ? 'Réinitialisation...' : 'Réinitialiser le Mot de Passe'}</Button>
        </form>
      </CardContent>
    </Card>
  );

  const renderSuccessStep = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center text-green-500"><CheckCircle className="mr-2 h-5 w-5" />Mot de Passe Réinitialisé</CardTitle>
        <CardDescription>Votre mot de passe a été mis à jour avec succès</CardDescription>
      </CardHeader>
      <CardContent>
        <Alert className="mb-4 border-green-500/20 bg-green-500/5"><CheckCircle className="h-4 w-4 text-green-500" /><AlertDescription>Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.</AlertDescription></Alert>
        <Button className="w-full bg-primary hover:bg-primary/90" asChild><Link to="/login">Se Connecter Maintenant</Link></Button>
      </CardContent>
    </Card>
  );

  const renderCurrentStep = () => {
    switch (step) {
      case 'identify': return renderIdentifyStep();
      case 'method': return renderMethodStep();
      case 'sent': return renderSentStep();
      case 'reset': return renderResetStep();
      case 'success': return renderSuccessStep();
      default: return renderIdentifyStep();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background/90 to-destructive/5 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <Link to="/login" className="inline-flex items-center text-muted-foreground hover:text-primary mb-6"><ArrowLeft className="mr-2 h-4 w-4" />Retour à la connexion</Link>
          <div className="flex justify-center mb-6"><PadaidLogo size="lg" showText={true} /></div>
          <h2 className="text-3xl font-bold text-foreground">Mot de Passe Oublié</h2>
          <p className="mt-2 text-muted-foreground">Réinitialisation sécurisée pour le fondateur</p>
        </div>
        {renderCurrentStep()}
        <Card className="bg-muted/50"><CardContent className="pt-6"><div className="flex items-center space-x-3"><Shield className="h-5 w-5 text-primary" /><div><p className="text-sm font-medium">Sécurité Renforcée</p><p className="text-xs text-muted-foreground">Accès réservé au fondateur Abakar ALI uniquement</p></div></div></CardContent></Card>
      </div>
    </div>
  );
};

export default ForgotPassword;